import { useSyncedStore } from "@syncedstore/react";
import { useState } from "react";
import { EDITOR_VIEW_NAME } from "../core/constants";
import { store } from "../core/state";
import { saveUserData } from "../core/code/saver";
import { syncedDataStore, isConnectedToParty } from "../features/collaboration/party";
import { CodeEditorMonaco } from "./CodeEditorMonaco";
import { EditorStatusBar } from "./EditorStatusBar";
import { MultiTabMonacoEditor } from "./MultiTabMonacoEditor";
import { getViewData } from "../core/views";

export const ProjectEditor = (props : { 
  width? : string,
  height?: string,
  currentTheme?: string,
  rightSidebarCollapsed?: boolean,
  onRightSidebarToggle?: () => void,
}) => {
  const [editorState, setEditorState] = store.useState(EDITOR_VIEW_NAME);
  const syncedState = useSyncedStore(syncedDataStore);

  const userCodeChanged = (code : string, tab? : any, index? : number) => {
    if (isConnectedToParty()) {
      syncedState.partyData.code = code;
      //TODO: support multitab editor
    }
    // WICHTIG: Hole rightTab direkt aus dem Store, nicht aus dem Closure (könnte veraltet sein)
    const currentStoreState = getViewData(EDITOR_VIEW_NAME);
    const currentRightTab = currentStoreState?.data?.rightTab;
    // Verwende den aktuellen Store-State, nicht den Closure-State
    const stateToUse = currentStoreState || editorState;
    const newState = { ...stateToUse };
    if (!tab || (index === undefined)) {
      // Stelle sicher, dass alle Properties erhalten bleiben
      const existingData = stateToUse.data || {};
      // WICHTIG: rightTab muss IMMER erhalten bleiben - hole es direkt aus dem Store
      newState.data = {
        ...existingData,
        editorCode : code,
        dirty : true,
        rightTab: currentRightTab !== undefined ? currentRightTab : existingData.rightTab
      }
    } else if (tab && index >= 0) {
        const existingData = stateToUse.data || {};
        const isV2 = stateToUse.project?.version === '2.0';
        
        if (isV2) {
          // V2 projects: Store edits in v2EditorChanges map (keyed by path)
          // This avoids re-rendering all tabs and corrupting Monaco models
          const v2Changes = { ...(existingData.v2EditorChanges || {}) };
          const key = tab.path || `idx-${index}`;
          v2Changes[key] = code;
          
          newState.data = {
            ...existingData,
            v2EditorChanges: v2Changes,
            dirty: true,
            rightTab: currentRightTab !== undefined ? currentRightTab : existingData.rightTab
          };
        } else {
          // Legacy projects: Update editorTabs array
          const updatedTabs = stateToUse.data.projectContent.tabs.map((projectTab : any, i : number) => {
            const newTab = { ...projectTab };
            newTab.index = i;
            if (i === index) {
              newTab.code = code;
            }
            return newTab;
          });
          
          newState.data = {
            ...existingData,
            editorTabs: updatedTabs,
            dirty: true,
            rightTab: currentRightTab !== undefined ? currentRightTab : existingData.rightTab
          };
        }
    }

    setEditorState(newState);
  }

  // Note: Do not mutate code state here; EditorView owns editor state lifecycle

  const cfg = editorState?.cfg;
  const lang = editorState?.project?.language;
  const options = editorState?.project?.options;
  const [status, setStatus] = useState<{lines:number; lineNumber:number; column:number}>({lines:0, lineNumber:1, column:1});
  const [modified, setModified] = useState<boolean>(false);

  const editorHeight = props.height || "100%";

  //const editorHeight = props.height || undefined;

  let editor = null;

  const isV2 = editorState?.project?.version === '2.0';
  const projectContentTabs = editorState?.data?.projectContent?.tabs;
  const v2EditorChanges = editorState?.data?.v2EditorChanges || {};
  
  // Für V2-Projekte: IMMER projectContent.tabs als Basis, v2EditorChanges für User-Änderungen
  // Für alte Projekte: editorTabs falls vorhanden, sonst projectContent.tabs
  let allTabs;
  if (isV2 && projectContentTabs) {
    // V2: Merge projectContent.tabs mit v2EditorChanges
    allTabs = projectContentTabs.map((t: any) => ({
      ...t,
      code: v2EditorChanges[t.path] !== undefined ? v2EditorChanges[t.path] : t.code
    }));
  } else {
    // Legacy: editorTabs als Fallback
    allTabs = editorState?.data?.editorTabs || projectContentTabs;
  }
  
  // Für V2-Projekte: Nutze openFilePaths (dynamisch geöffnete Dateien) oder active: true als Fallback
  // Für alte Projekte: Alle Tabs anzeigen (Rückwärtskompatibilität)
  const openFilePaths = editorState?.data?.openFilePaths;
  const activeFilePath = editorState?.data?.activeFilePath;
  const tabs = isV2
    ? (openFilePaths !== undefined
        ? (openFilePaths.length > 0
            ? allTabs?.filter((t: any) => openFilePaths.includes(t.path))
            : [])
        : allTabs?.filter((t: any) => t.active === true))
    : allTabs;
  const hasTabs = tabs && tabs.length > 0;
  
  // Berechne den aktiven Tab-Index basierend auf activeFilePath
  const activeTabIndex = isV2 && activeFilePath && tabs
    ? tabs.findIndex((t: any) => t.path === activeFilePath)
    : undefined;
  
  // Handler für Tab-Schließen (nur für V2-Projekte)
  const handleTabClose = async (index: number, tab: any) => {
    if (!isV2) return;
    // Persistieren vor dem Schließen: VFS + LS + Cloud, damit Änderungen nach Reload erhalten bleiben
    await saveUserData();
    // Entferne die Datei aus openFilePaths (erlaubt auch: 0 Tabs)
    const currentOpenPaths = editorState?.data?.openFilePaths !== undefined
      ? editorState?.data?.openFilePaths
      : (projectContentTabs?.filter((t: any) => t.active).map((t: any) => t.path) || []);

    const finalOpenPaths = currentOpenPaths.filter((p: string) => p !== tab.path);

    setEditorState({
      ...editorState,
      data: {
        ...editorState?.data,
        openFilePaths: finalOpenPaths,
        activeFilePath: finalOpenPaths[0] || null
      }
    });
  };
  
  // Handler für Tab-Wechsel in MultiTabMonacoEditor (V2-Projekte)
  const handleActiveIndexChange = (newIndex: number) => {
    if (!isV2 || !tabs || !tabs[newIndex]) return;
    
    const newActiveTab = tabs[newIndex];
    if (newActiveTab.path && newActiveTab.path !== activeFilePath) {
      setEditorState({
        ...editorState,
        data: {
          ...editorState?.data,
          activeFilePath: newActiveTab.path
        }
      });
    }
  };

  // Multi-Tab Editor (V2 und alte Multi-Tab Projekte)
  if (hasTabs) {
    editor = (
      <MultiTabMonacoEditor 
        tabs={tabs} 
        cfg={cfg} 
        options={options} 
        width={props.width} 
        height={editorHeight} 
        codeChanged={userCodeChanged} 
        currentTheme={props.currentTheme}
        rightSidebarCollapsed={props.rightSidebarCollapsed}
        onRightSidebarToggle={props.onRightSidebarToggle}
        closableTabs={isV2}
        onTabClose={handleTabClose}
        activeIndex={activeTabIndex !== undefined && activeTabIndex >= 0 ? activeTabIndex : undefined}
        onActiveIndexChange={isV2 ? handleActiveIndexChange : undefined}
      />
    );
  } 
  // Single-File Projekte (kein Multi-Tab)
  else if (!hasTabs && !isV2 && editorState?.data?.projectContent?.projectSource) {
    const code = isConnectedToParty() ? syncedState.partyData.code : (editorState?.data?.editorCode ?? editorState?.data?.projectContent?.projectSource);
    editor = (
      <>
        <CodeEditorMonaco
          code={code}
          cfg={cfg}
          projectOptions={options}
          mode={lang}
          width={props.width}
          height={editorHeight}
          codeChanged={userCodeChanged}
          onStatusChange={(s) => setStatus(s)}
          onModifiedChange={(m) => setModified(m)}
          currentTheme={props.currentTheme}
        />
        <EditorStatusBar status={status} rightText={modified ? 'Bearbeitet' : 'Gespeichert'} />
      </>
    );
  }

  return (editor);
}
